import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Alert,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  VolunteerActivism,
  People,
  Work,
  EmojiEvents,
  Phone,
  Email,
  LocationOn,
  CheckCircle,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ContactTiles from '../components/shared/ContactTiles';

const VolunteerPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Hero Section */}
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Box display="flex" justifyContent="center" mb={3}>
          <VolunteerActivism sx={{ fontSize: 64, color: 'primary.main' }} />
        </Box>
        <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Volunteer With Us
        </Typography>
        <Typography variant="h5" color="text.secondary" paragraph sx={{ maxWidth: 800, mx: 'auto' }}>
          Make a difference in thousands of lives and feel great about yourself
        </Typography>
        <Typography variant="body1" sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
          The <strong>Foodbarn</strong> needs volunteers! Any age welcome. Do you need to get out and feel great about yourself? 
          Come and help the team making a difference in thousands of lives.
        </Typography>
      </Box>

      {/* Urgent Volunteer Alert */}
      <Box sx={{ mb: 6 }}>
        <Alert severity="warning" sx={{ fontSize: '1rem' }}>
          <Box display="flex" alignItems="center" gap={2}>
            <VolunteerActivism sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                🚨 WE URGENTLY NEED VOLUNTEERS!
              </Typography>
              <Typography variant="body1" paragraph>
                The <strong>Foodbarn</strong> needs volunteers! Any age welcome. Do you need to get out and feel great about yourself? 
                Come and help the team making a difference in thousands of lives.
              </Typography>
              <Typography variant="body2" paragraph>
                We are a registered workplace for Community Services, Centrelink & SPER.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/volunteer/apply')}
                sx={{ mt: 1 }}
              >
                Apply to Volunteer Today!
              </Button>
            </Box>
          </Box>
        </Alert>
      </Box>

      {/* Why Volunteer Section */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: 4 }}>
          Why Volunteer at New Life Foodbarn?
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <People sx={{ fontSize: 40, color: 'primary.main' }} />
                  <Typography variant="h5" component="h3">
                    Community Impact
                  </Typography>
                </Box>
                <Typography variant="body1" paragraph>
                  Help feed thousands of mouths a month and support over 17 charities in our community.
                </Typography>
                <Typography variant="body1" paragraph>
                  Be part of a team that saves 25,000 to 30,000 tons of waste food from going to landfill each month.
                </Typography>
                <Typography variant="body1">
                  Make a real difference in the lives of people experiencing financial difficulties, pensioners, 
                  and health care and senior card holders.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <EmojiEvents sx={{ fontSize: 40, color: 'primary.main' }} />
                  <Typography variant="h5" component="h3">
                    Personal Benefits
                  </Typography>
                </Box>
                <Typography variant="body1" paragraph>
                  <em>"Do you need to get out and feel great about yourself?"</em>
                </Typography>
                <Typography variant="body1" paragraph>
                  Join a supportive community of 15-25 committed volunteers working together each day.
                </Typography>
                <Typography variant="body1" paragraph>
                  Gain valuable experience in community service and food distribution.
                </Typography>
                <Typography variant="body1">
                  Be part of an organization that has been serving the Gold Coast community with love, 
                  compassion, and dedication for over 20 years.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* What We Do Section */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: 4 }}>
          What We Do
        </Typography>
        <Card sx={{ p: 4 }}>
          <CardContent>
            <Typography variant="body1" paragraph>
              New Life Foodbarn is a registered charity that provides affordable fresh food to the Gold Coast community. 
              We source quality produce from local markets and handpicked goods, making them accessible to everyone.
            </Typography>
            <Typography variant="body1" paragraph>
              Our mission is to reduce food waste while helping people in need. We work with over 17 charities 
              and serve thousands of people each month, including pensioners, healthcare card holders, and those experiencing financial difficulties.
            </Typography>
            <Typography variant="body1" paragraph>
              We are a registered workplace for Community Services, Centrelink & SPER, making us an ideal 
              volunteering opportunity for those looking to give back to the community.
            </Typography>
            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>Our Mission:</strong> To provide affordable fresh food from local markets and handpicked goods
                to the Gold Coast community while reducing food waste and supporting those in need.
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      </Box>

      {/* Volunteer Roles Section */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: 4 }}>
          Volunteer Roles & Responsibilities
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Work sx={{ fontSize: 40, color: 'primary.main' }} />
                  <Typography variant="h5" component="h3">
                    Food Operations
                  </Typography>
                </Box>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Sorting and organizing food items" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Assisting with food parcel preparation" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Helping customers during shop hours" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Supporting delivery and pickup services" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <People sx={{ fontSize: 40, color: 'primary.main' }} />
                  <Typography variant="h5" component="h3">
                    Community Support
                  </Typography>
                </Box>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Welcoming and assisting visitors" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Administrative support and organization" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Supporting special events and programs" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Contributing to our 12-step recovery program" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Success Stories Section */}
      <Box sx={{ mb: 6 }}>
        <Alert severity="success" sx={{ mb: 4, fontSize: '1rem' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            Success Stories
          </Typography>
          <Typography variant="body1">
            Our volunteers have helped us serve over 17 charities and thousands of community members. 
            Join our team and be part of making a real difference in people's lives.
          </Typography>
        </Alert>
      </Box>

      {/* Contact Information */}
      <ContactTiles title="Get in Touch" />

      {/* Call to Action */}
      <Box sx={{ textAlign: 'center', mt: 6 }}>
        <Card sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', p: 4 }}>
          <CardContent>
            <Typography variant="h4" component="h2" gutterBottom>
              Ready to Make a Difference?
            </Typography>
            <Typography variant="h6" paragraph>
              Join our volunteer team today and help us serve the Gold Coast community.
            </Typography>
            <Typography variant="body1" paragraph>
              We welcome volunteers of all ages and backgrounds. No experience necessary - 
              we provide training and support to help you succeed.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/volunteer/apply')}
              sx={{
                mt: 2,
                py: 1.5,
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'grey.100'
                }
              }}
            >
              Apply to Volunteer Today!
            </Button>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default VolunteerPage; 